/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-explicit-any */

import Button from "components/Button";
import Form from "components/Form";
import Label from "components/Label";

import { useSession } from "hooks/use-session";

import React, { FC, useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  formatErrors,
  hasAddressAttirbute,
  parseAdrAddress,
  Place,
  required
} from "utils/form";
import { purgeObject } from "utils/object";
import { getQueryString } from "utils/url";

import axios from "axios";
import { useRouter } from "next/router";
import useAsync from "hooks/use-async";
import segment from "lib/segment";
import styles from "./styles.module.scss";
import WaitlistForm from "../WaitlistForm";

interface FormValues {
  firstname: string;
  lastname: string;
  phone: string;
  place: Place;
  email: string;
}

const AccountDetails: FC = () => {
  const [formState, setFormState] = useState({});
  const [formValues, setFormValues] = useState<FormValues | undefined>();
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [isWaitlistZip, setIsWaitlistZip] = useState<React.ReactNode | null>(
    false
  );

  const { utm } = useSession();
  const router = useRouter();
  const { query } = router;

  function formatPayload(values: FormValues) {
    const { firstname, lastname, phone, place, email } = values;

    const { state, zip, city, address } = parseAdrAddress(place.adr_address);

    const formatZip = !!zip?.includes("-");

    return {
      address,
      city,
      zip: formatZip ? zip?.split("-")[0] : zip,
      state,

      place,

      firstname,
      lastname,
      email,
      phone,

      ...purgeObject(utm)
    };
  }

  async function validateZip(zip: string | undefined) {
    const zipCodeResponse = await axios.get(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/onboarding/form/?zip_code=${zip}`
    );

    const { is_supported: isSupported } = zipCodeResponse?.data || {};

    return isSupported;
  }

  const [submit, submitting] = useAsync(async (values: FormValues) => {
    setError(null);

    const payload = formatPayload(values);
    setFormValues(values);

    const isValid = await validateZip(payload.zip);

    if (!isValid) {
      setIsWaitlistZip(true);
      return;
    }

    const response = await axios
      .post(
        `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/onboarding/form/`,
        payload
      )
      .catch(() => {
        setError(
          <>
            Please review the information provided. If you&apos;ve tried more
            than once, please <a href="/contact">contact us</a>.
          </>
        );
      });

    if (response) {
      const { data } = response;
      const { home_id: homeID } = data;
      setFormState({ ...formState, ...payload, homeID });

      segment.track("Account Details Confirmed", query);

      router.replace(
        `${router.pathname}?${getQueryString({
          ...query,
          hid: homeID
        })}`
      );
    }
  });

  async function goToCheckout(hid: string) {
    const stripeResponse = await axios.post(
      `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/stripe_checkout/`,
      {
        home_id: hid,
        price_id: query.pid,
        cancel_url: window.location.href
      }
    );

    if (stripeResponse) {
      const { session_url: sessionURL } = stripeResponse.data;
      window.open(sessionURL, "_self");
    }
  }

  useEffect(() => {
    if (query.hid) {
      goToCheckout(String(query.hid));
    }
  }, [query.hid]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <div className={styles.leftPanel}>
          <div className={styles.imageContainer}>
            <img alt="" src="/home-shape.png" className={styles.desktop} />
          </div>
        </div>

        <div className={styles.rightPanel}>
          {isWaitlistZip ? (
            <div className={styles.formTile}>
              <WaitlistForm userInfo={formValues} />
            </div>
          ) : (
            <div className={styles.formTile}>
              <Label
                className={styles.formTitle}
                variant="h6-centered"
                color="primary2"
              >
                Confirm Home Details
              </Label>
              <div className={styles.waitlistDescription}>
                Just share a few pieces of your information so we can get your
                home up and humming
              </div>
              {error && (
                <Label className={styles.formError} variant="s" renderNode>
                  {error}
                </Label>
              )}
              <Form
                id="lead_flow"
                initialValues={formState}
                className={styles.form}
                onSubmit={submit}
                onError={(errors) =>
                  toast.error(formatErrors(errors), { position: "top-right" })
                }
                fields={[
                  {
                    type: "text",
                    id: "firstname",
                    props: { required: true },
                    placeholder: "First name"
                  },
                  {
                    type: "text",
                    id: "lastname",
                    props: { required: true },
                    placeholder: "Last name"
                  },
                  {
                    type: "email",
                    id: "email",
                    props: { required: true },
                    placeholder: "Email address"
                  },
                  {
                    type: "location",
                    id: "place",
                    placeholder: "Address",
                    validate: [
                      required("Address field is required"),
                      hasAddressAttirbute(
                        ["zip"],
                        "Invalid address, missing zip code"
                      ),
                      hasAddressAttirbute(
                        ["city"],
                        "Invalid address, missing city"
                      ),
                      hasAddressAttirbute(
                        ["state"],
                        "Invalid address, missing state"
                      )
                    ]
                  },
                  {
                    type: "text",
                    id: "phone",
                    placeholder: "Phone number",
                    props: { required: true }
                  }
                ]}
              >
                {({ invalid }) => (
                  <div className={styles.formFooter}>
                    <Button
                      loading={submitting}
                      type="submit"
                      className={styles.submit}
                      disabled={invalid}
                      variant="accent"
                    >
                      Join Now
                    </Button>
                  </div>
                )}
              </Form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;
